/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { PriceDisplay } from '@/components/blocks/PriceDisplay';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { getProductDisplayInfo } from '@/utils/getProductDisplayInfo';
import { isKitOrBundleType } from '@/utils/productIsA';
import { Box, Stack, Typography } from '@mui/material';
import { FC, useContext, useMemo } from 'react';
import { pdpOfferPriceDisplaySX, pdpListPriceDisplaySX } from './styles/priceDisplay';
import { srOnly } from '../Common/styles/commonStyles';
import { formatPrice } from '@/utils/formatPrice';
import { useNextRouter } from '@/data/Content/_NextRouter';

export const ProductDetailsPrice: FC = () => {
	const localization = useLocalization('PriceDisplay');
	const router = useNextRouter();	
	const { selection, product } = useContext(ContentContext) as ReturnType<
		typeof useProductDetails
	> &
		ReturnType<typeof useSkuListTable>;
	const isKitOrBundle = isKitOrBundleType[product?.type ?? 'unknown'];
	const sku = !isKitOrBundle ? selection?.sku : product;
	const { offer, list, currency } = useMemo<{
		offer: number;
		list: number;
		currency: string;
	}>(() => {
		const { prices } = getProductDisplayInfo(sku, product);
		const defaults = { offer: 0, list: 0, currency: '' };
		return { ...defaults, ...prices };
	}, [sku, product]);
	const locale = router.locale ?? router.defaultLocale;
	const eaaOffer = formatPrice(locale, currency, offer);
	const eaaList = formatPrice(locale, currency, list);  

	return (
		<Stack direction="row" alignItems="center">

			{offer > 0 ? (				
				<Box>
					<Box sx={srOnly} >
						{(offer < list)?localization.Labels.EaaListAndOfferPriceText.t({ eaaOffer,eaaList }):localization.Labels.EaaOfferPriceText.t({ eaaOffer })}				
					</Box>
					<Typography 
					variant="body1" 
					component="span" 
					data-testid="offer-price" 
					id="offer-price" 
					aria-hidden="true"				
					sx={pdpOfferPriceDisplaySX({list, offer})}>
						<PriceDisplay currency={currency} min={offer} />
					</Typography>
				</Box>				
			) : null}

			{offer > 0 && offer < list ? (
				<Typography
					variant="body1"
					component="span"
					sx={pdpListPriceDisplaySX}
					data-testid="list-price"
					aria-hidden="true"		
					id="list-price"
				>
					<PriceDisplay currency={currency} min={list} />
				</Typography>
			) : null}

			{offer === 0 ? (
				<Typography variant="body1" component="span" data-testid="no-price" id="no-price">
					{localization.Labels.Pending.t()}
				</Typography>
			) : null}
		</Stack>
	);
};
